import type {
  ServiceHealthRow,
  ServiceHealthServiceId,
  ServiceHealthStatus,
} from '@/versions/v1/shared/admin-seed';
import { ADMIN_SEED_ANCHOR_MS, PROBE_STALE_MS } from '@/versions/v1/shared/admin-seed';

export { PROBE_STALE_MS };

export const SERVICE_HEALTH_SERVICES = ['daytona', 'claude', 'google'] as const;

export const SERVICE_TO_PROVIDER = {
  daytona: 'daytona',
  claude: 'claude',
  google: 'google',
} as const;

export const PROVIDER_LABELS: Record<
  (typeof SERVICE_TO_PROVIDER)[ServiceHealthServiceId],
  string
> = {
  daytona: 'Daytona (sandbox)',
  claude: 'Claude (Anthropic)',
  google: 'Google Gemini',
};

export function formatRelative(epochMs: number, now = ADMIN_SEED_ANCHOR_MS): string {
  const delta = now - epochMs;
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

export function keyStatusLabel(keyStatus: ServiceHealthRow['keyStatus']): string {
  switch (keyStatus) {
    case 'configured':
      return 'Key: admin DB';
    case 'env':
      return 'Key: env fallback';
    case 'missing':
      return 'No key';
  }
}

export function detailChips(row: ServiceHealthRow): string[] {
  const chips: string[] = [];
  if (row.details.kind === 'daytona') {
    chips.push(`Host: ${row.details.apiHost}`);
    chips.push(`Backend: ${row.details.sandboxBackend}`);
  } else if (row.details.kind === 'google') {
    chips.push(`max_llm_calls: ${row.details.maxLlmCalls}`);
    chips.push(`Backend: ${row.details.sandboxBackend}`);
  } else {
    chips.push(`Backend: ${row.details.sandboxBackend}`);
    chips.push(row.details.bedrockModeActive ? 'Auth: Bedrock' : 'Auth: native');
  }
  return chips;
}

export function statusRail(status: ServiceHealthStatus): string {
  switch (status) {
    case 'up':
      return 'border-l-emerald-500';
    case 'degraded':
      return 'border-l-amber-500';
    case 'down':
      return 'border-l-red-500';
    default:
      return 'border-l-zinc-300 dark:border-l-zinc-700';
  }
}

export function fallbackRow(serviceId: ServiceHealthServiceId): ServiceHealthRow {
  return {
    service: serviceId,
    status: 'down',
    keyStatus: 'missing',
    kitEnabled: false,
    probe: null,
    probeStale: false,
    userImpact: '',
    details:
      serviceId === 'google'
        ? { kind: 'google', maxLlmCalls: 50, sandboxBackend: 'stub' }
        : serviceId === 'daytona'
          ? { kind: 'daytona', apiHost: 'unknown', sandboxBackend: 'stub' }
          : { kind: 'claude', sandboxBackend: 'stub', bedrockModeActive: false },
  };
}

export function healthSummary(rows: ServiceHealthRow[]): {
  dot: string;
  text: string;
} {
  const total = rows.length;
  const downCount = rows.filter((r) => r.status === 'down').length;
  const degradedCount = rows.filter((r) => r.status === 'degraded').length;
  if (downCount > 0) {
    return {
      dot: 'bg-red-500',
      text: `${downCount} of ${total} ${downCount === 1 ? 'service down' : 'services down'}`,
    };
  }
  if (degradedCount > 0) {
    return { dot: 'bg-amber-500', text: `${degradedCount} of ${total} degraded` };
  }
  return { dot: 'bg-emerald-500', text: `All ${total} services operational` };
}

export function orderedRows(
  services: ServiceHealthRow[],
): ServiceHealthRow[] {
  return SERVICE_HEALTH_SERVICES.map(
    (serviceId) =>
      services.find((s) => s.service === serviceId) ?? fallbackRow(serviceId),
  );
}

export function mockRefreshServices(
  services: ServiceHealthRow[],
): ServiceHealthRow[] {
  return services.map((row) =>
    row.probe
      ? {
          ...row,
          probe: {
            ...row.probe,
            checkedAt: ADMIN_SEED_ANCHOR_MS,
          },
        }
      : row,
  );
}

export function mockTestService(
  services: ServiceHealthRow[],
  service: ServiceHealthServiceId,
): ServiceHealthRow[] {
  return services.map((row) =>
    row.service === service
      ? {
          ...row,
          status: 'up',
          probe: {
            status: 'ok',
            checkedAt: ADMIN_SEED_ANCHOR_MS,
            errorCode: null,
            errorMessage: null,
          },
          probeStale: false,
        }
      : row,
  );
}
